import { ContextEnum } from 'api/files';
import { GenericDocumentUploader } from 'components/domain/Document/GenericDocumentUploader';
import { TrashIcon } from 'components/icons/Icons';
import { Button } from 'components/ui/Button';
import { FC, useCallback } from 'react';
import { filesAPI } from 'services/swagger-api';
import { CLAIMS_STATEMENT_FILE_TYPE, useClaimsStatementDocument } from './useClaimsStatementDocument';

const DOCUMENT_NAME = "Relevé d'information de sinistralité";

interface ClaimsStatementUploaderProps {
  /** Devis qui porte le justificatif. */
  quoteId?: string;
  /** Dépôt ou retrait enregistré : les données de contractualisation se relisent (carte risque). */
  onChanged: () => Promise<void> | void;
  /** Consultation seule quand le courtier n'a pas la main sur le risque. */
  disabled?: boolean;
}

/**
 * Dépôt du relevé d'information de sinistralité sur le devis, depuis la contractualisation :
 * obligatoire dans tous les cas, c'est lui qui débloque
 * l'envoi du contrat. Un nouveau dépôt remplace le précédent côté serveur.
 */
export const ClaimsStatementUploader: FC<ClaimsStatementUploaderProps> = ({ quoteId, onChanged, disabled }) => {
  const { data, reload } = useClaimsStatementDocument(quoteId);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!quoteId) return { success: false, error: 'Devis introuvable.' };

      const response = await filesAPI.uploadFile({
        fileType: CLAIMS_STATEMENT_FILE_TYPE,
        context: ContextEnum.Quote,
        objetId: quoteId,
        file
      });

      if (!response.isSuccess) {
        return { success: false, error: response.errorMessage || 'Le dépôt du relevé a échoué.' };
      }

      await reload();
      await onChanged();
      return { success: true };
    },
    [quoteId, reload, onChanged]
  );

  const handleRemove = useCallback(
    async (storedFileId: string) => {
      await filesAPI.deleteFileByStoredFileId({ storedFileId });
      await reload();
      await onChanged();
    },
    [reload, onChanged]
  );

  return (
    <div className="mt-3">
      <GenericDocumentUploader
        documentName={DOCUMENT_NAME}
        fileType={CLAIMS_STATEMENT_FILE_TYPE}
        storedFileId={data?._exists ? data.storedFileId ?? undefined : undefined}
        onUpload={handleUpload}
        onRemove={disabled ? undefined : handleRemove}
        disabled={disabled}
        acceptedFormats=".pdf"
        uploadLabel="Déposer"
        renderUploadedActions={(_storedFileId, onRemoveFile) => (
          <div className="flex items-center gap-3 text-sm">
            {data?.fileName && <span className="max-w-[16rem] truncate text-gray-600">{data.fileName}</span>}
            {data?.url && (
              <a className="text-primary hover:underline" href={data.url} target="_blank" rel="noreferrer">
                Voir
              </a>
            )}
            {onRemoveFile && (
              <Button type="button" plain onClick={onRemoveFile} disabled={disabled} title="Retirer le relevé">
                <TrashIcon className="h-4 w-4 text-gray-500 hover:text-red-500" />
              </Button>
            )}
          </div>
        )}
      />
    </div>
  );
};
